class ScentFinder extends HTMLElement {
  constructor() {
    super();
    this.answers = { gender: null, occasion: null, family: null };
    this.stepOrder = ['0', '1', '2', 'results'];

    this.productsDataEl = this.querySelector('[data-scent-finder-products]');
    this.products = [];
    try {
      this.products = JSON.parse(this.productsDataEl?.textContent || '[]');
    } catch (error) {
      this.products = [];
    }

    this.resultsGrid = this.querySelector('[data-scent-finder-results]');
    this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

    this.addEventListener('change', this.onOptionChange.bind(this));
    this.addEventListener('click', this.onResultsClick.bind(this));
    this.querySelectorAll('[data-scent-finder-back]').forEach((button) => {
      button.addEventListener('click', this.goBack.bind(this));
    });
    this.querySelectorAll('[data-scent-finder-restart]').forEach((button) => {
      button.addEventListener('click', this.restart.bind(this));
    });
  }

  onOptionChange(event) {
    const input = event.target;
    if (input.tagName !== 'INPUT' || input.type !== 'radio') return;

    const step = input.closest('.scent-finder__step');
    const question = step?.querySelector('[data-question]')?.dataset.question;
    if (!question) return;

    this.answers[question] = input.value;

    window.setTimeout(() => {
      if (question === 'family') {
        this.showResults();
      } else {
        this.goToStep(this.nextStep(step.dataset.step));
      }
    }, 220);
  }

  nextStep(currentStep) {
    const index = this.stepOrder.indexOf(currentStep);
    return this.stepOrder[Math.min(index + 1, this.stepOrder.length - 1)];
  }

  previousStep(currentStep) {
    const index = this.stepOrder.indexOf(currentStep);
    return this.stepOrder[Math.max(index - 1, 0)];
  }

  goBack(event) {
    const step = event.target.closest('.scent-finder__step');
    this.goToStep(this.previousStep(step.dataset.step));
  }

  goToStep(step) {
    this.querySelectorAll('.scent-finder__step').forEach((el) => {
      el.classList.toggle('is-active', el.dataset.step === step);
    });

    this.querySelectorAll('[data-dot]').forEach((dot) => {
      const dotIndex = Number(dot.dataset.dot);
      const currentIndex = this.stepOrder.indexOf(step);
      dot.classList.toggle('is-active', dotIndex === currentIndex);
      dot.classList.toggle('is-complete', dotIndex < currentIndex);
    });

    const stepLabel = this.querySelector('[data-scent-finder-step-label]');
    if (stepLabel) {
      const currentIndex = this.stepOrder.indexOf(step);
      stepLabel.textContent = step === 'results'
        ? 'Your matches'
        : `Step ${currentIndex + 1} of 3`;
    }

    const nextFocusable = this.querySelector(
      `.scent-finder__step[data-step="${step}"] input, .scent-finder__step[data-step="${step}"] button`
    );
    nextFocusable?.focus();
  }

  restart() {
    this.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.checked = false;
    });
    this.answers = { gender: null, occasion: null, family: null };
    this.goToStep('0');
  }

  scoreProduct(product) {
    const tags = (product.tags || []).map((tag) => tag.toLowerCase());
    let score = 0;
    const matched = new Set();

    if (this.answers.gender && this.answers.gender !== 'unisex') {
      if (tags.includes(this.answers.gender)) {
        score += 3;
        matched.add(this.answers.gender);
      } else if (tags.includes('unisex')) {
        score += 1;
        matched.add('unisex');
      }
    } else if (tags.includes('unisex')) {
      score += 1;
      matched.add('unisex');
    }

    if (this.answers.occasion && tags.includes(this.answers.occasion)) {
      score += 2;
      matched.add(this.answers.occasion);
    }

    if (this.answers.family) {
      this.answers.family.split(',').forEach((familyTag) => {
        if (tags.includes(familyTag)) {
          score += 2;
          matched.add(familyTag);
        }
      });
    }

    if (tags.includes('bestseller') || tags.includes('signature')) {
      score += 0.5;
    }

    return { score, matched: Array.from(matched) };
  }

  /* Plain-language labels for the internal tag vocabulary — shown to shoppers
     instead of raw tags like "for-him · office · fresh · aquatic". */
  static MATCH_LABELS = {
    'for-him': 'made for him',
    'for-her': 'made for her',
    unisex: 'unisex',
    'daily-wear': 'everyday wear',
    office: 'office-ready',
    'date-night': 'date-night',
    evening: 'evening & formal',
    woody: 'woody',
    amber: 'amber',
    oriental: 'oriental',
    resinous: 'resinous',
    leathery: 'leathery',
    floral: 'floral',
    fruity: 'fruity',
    sweet: 'sweet',
    'amber-sweet': 'amber-sweet',
    fresh: 'fresh',
    aquatic: 'aquatic',
    aromatic: 'aromatic',
    spicy: 'spicy',
    musky: 'musky',
    powdery: 'powdery',
    oud: 'oud',
  };

  describeMatch(matched) {
    const labels = matched
      .map((tag) => ScentFinder.MATCH_LABELS[tag])
      .filter(Boolean);
    if (labels.length === 0) return 'A solid all-round pick from your answers.';
    if (labels.length === 1) return `Matched on: ${labels[0]}.`;
    const last = labels[labels.length - 1];
    const rest = labels.slice(0, -1);
    return `Matched on: ${rest.join(', ')} and ${last}.`;
  }

  showResults() {
    const scored = this.products
      .map((product) => ({ product, ...this.scoreProduct(product) }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);

    this.resultsGrid.innerHTML = '';

    if (scored.length === 0) {
      this.resultsGrid.innerHTML = '<p class="scent-finder__empty">Browse the full collection to find your match.</p>';
    } else {
      scored.forEach(({ product, matched }, index) => {
        const card = document.createElement('div');
        card.className = 'scent-finder__result-card';

        const badgeLabel = index === 0 ? 'Best match' : 'Also worth trying';
        const matchExplanation = this.describeMatch(matched);
        const canQuickAdd = Boolean(product.available && product.variantId);
        const quickAddLabel = canQuickAdd ? 'Quick Add to Bag' : 'Sold Out';
        const quickAddAttrs = canQuickAdd
          ? `data-quick-add-variant="${product.variantId}"`
          : 'disabled';

        card.innerHTML = `
          <a href="${product.url}" class="scent-finder__result-link">
            <span class="scent-finder__result-badge${index === 0 ? ' is-primary' : ''}">${badgeLabel}</span>
            ${product.image ? `<img src="${product.image}" alt="${product.title}" loading="lazy">` : ''}
            <p class="scent-finder__result-title">${product.title}</p>
            <p class="scent-finder__result-tags">${matchExplanation}</p>
            <span class="scent-finder__result-price">${product.price}</span>
          </a>
          <button type="button" class="button button--secondary scent-finder__quick-add" ${quickAddAttrs}>${quickAddLabel}</button>
        `;

        this.resultsGrid.appendChild(card);
      });
    }

    this.goToStep('results');
  }

  onResultsClick(event) {
    const button = event.target.closest('[data-quick-add-variant]');
    if (!button || button.disabled) return;
    event.preventDefault();
    this.quickAdd(button);
  }

  quickAdd(button) {
    const variantId = button.dataset.quickAddVariant;
    if (!variantId) return;

    const originalLabel = button.dataset.originalLabel || button.textContent.trim();
    button.dataset.originalLabel = originalLabel;
    button.disabled = true;
    button.classList.remove('is-error', 'is-added');
    button.classList.add('is-loading');
    button.textContent = 'Adding…';

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);
    if (this.cart) {
      formData.append(
        'sections',
        this.cart.getSectionsToRender().map((section) => section.id)
      );
      formData.append('sections_url', window.location.pathname);
      this.cart.setActiveElement(document.activeElement);
    }
    config.body = formData;

    const resetAfter = (delay) => {
      window.setTimeout(() => {
        button.textContent = originalLabel;
        button.classList.remove('is-loading', 'is-error', 'is-added');
        button.disabled = false;
      }, delay);
    };

    fetch(routes.cart_add_url, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          button.textContent = response.description || 'Sold out';
          button.classList.remove('is-loading');
          button.classList.add('is-error');
          resetAfter(2200);
          return;
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'scent-finder',
          productVariantId: variantId,
          cartData: response,
        });

        if (this.cart) {
          this.cart.renderContents(response);
          button.textContent = 'Added ✓';
          button.classList.remove('is-loading');
          button.classList.add('is-added');
          resetAfter(1800);
        } else {
          window.location = routes.cart_url;
        }
      })
      .catch(() => {
        button.textContent = 'Error — try again';
        button.classList.remove('is-loading');
        button.classList.add('is-error');
        resetAfter(2200);
      });
  }
}

customElements.define('scent-finder', ScentFinder);

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-scent-finder-open]');
  if (!trigger) return;

  event.preventDefault();

  const parentMegaMenu = trigger.closest('details.mega-menu');
  parentMegaMenu?.removeAttribute('open');

  document.getElementById('HeaderMenu-scent-finder')?.click();
});
